import { useEffect } from 'react';
import { InteractionStatus } from '@azure/msal-browser';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { msalConfig } from '@/authConfig';
import useIsRegistryRoute from '../useIsRegistryRoute';

interface Props {
  onClearCredentials: () => void;
  onTokenAcquired: (token: string) => void;
}

export function useMsalTokenAcquisition({
  onClearCredentials,
  onTokenAcquired,
}: Props) {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const isRegistryRoute = useIsRegistryRoute();

  useEffect(() => {
    const accountOfEnv = accounts.find(
      (acc) =>
        acc.environment ===
        new URL(process.env.NEXT_PUBLIC_B2C_BASE_URL ?? '').host,
    );
    if (inProgress !== InteractionStatus.None) return;

    if (isAuthenticated && !!accountOfEnv) {
      instance
        .acquireTokenSilent({
          account: accountOfEnv,
          scopes: msalConfig.auth.scopes,
        })
        .then((authResult) => {
          onTokenAcquired(authResult.accessToken);
        })
        .catch(() => {
          if (isRegistryRoute) {
            instance.acquireTokenRedirect({
              scopes: msalConfig.auth.scopes,
            });
          } else {
            onClearCredentials();
          }
        });
    } else {
      if (isRegistryRoute) {
        instance.acquireTokenRedirect({
          scopes: msalConfig.auth.scopes,
        });
      } else {
        onClearCredentials();
      }
    }
  }, [
    accounts,
    instance,
    isAuthenticated,
    inProgress,
    isRegistryRoute,
    onClearCredentials,
    onTokenAcquired,
  ]);

  // sync all tabs logout
  useEffect(() => {
    function listener(e: StorageEvent) {
      if (e.key?.startsWith('msal') && e.newValue === null) {
        if (isRegistryRoute) {
          instance.acquireTokenRedirect({
            scopes: msalConfig.auth.scopes,
          });
        } else {
          onClearCredentials();
        }
      }
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('storage', listener);
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('storage', listener);
      }
    };
  }, [instance, isRegistryRoute, onClearCredentials]);
}
